import React, { useRef, useEffect, useState, useMemo, useCallback, CSSProperties } from 'react';

import TitleEditor from './TitleEditor';
import styled from 'styled-components/macro';
import ScreenshotEditor from './ScreenshotEditor';
import SearchResults from './SearchResults';
import DescriptionEditor from './DescriptionEditor';
import ActionRecorder from './ActionRecorder';
import AttachmentList from './AttachmentList';
import PrivacyDisclosure from '../../App/PrivacyDisclosure';
import { ResourceStrings } from '../../../ResourceStrings';
import { App, SignInState } from '../../../app';
import Button from '../../shared/Button';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import { AppState, Dialog, OperationState, ActionRecorderState, ReportType, SearchInfoTarget, ReleaseAction, Sort } from '../../../store/appState';
import * as Actions from '../../../store/actions';
import PostDetail from '../PostDetail';
import Accordion from '../../shared/Accordion';
import { AuthenticationMessages, Notification } from './Notifications';
import { ChildrenProps } from '../../../utils/types';
import CommentSubmittedDialog from './CommentSubmittedDialog';
import PostSubmittedDialog from './PostSubmittedDialog';
import { platformActions } from '../../../communication/Communication';
import { useQueryParam, usePreventPageClose } from '../../../hooks/routing';
import { UrlParam, EntryValue, postTypes, ProductInfoExceptionValues, tagParams } from '../../../constants/urlParams';
import { minimumPostDetailsLength, minimumPostTitleLength, maximumPostTitleLength } from '../../../constants/validation';
import PageContent from '../PageContent';
import { useHistory, Prompt, useLocation, useParams } from 'react-router-dom';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { ProblemStates } from '../../../constants/feedback';
import { testFeedbackTitlePrefix } from '../../../constants/config';
import NotificationDialog from '../../shared/NotificationDialog';
import { Icon } from '@fluentui/react/lib/Icon';
import { AppPaths, codeOfConduct } from '../../../constants/urls';
import GoodReportInstructions from './GoodReportInstructions';
import { forMinWidth } from '../../../theme/breakpoints';
import Panel from '../../shared/Panel';
import { SpaceId } from '../../../config/spaces/spaceIds';
import { getSpaceConfig, instance, getSpaceNameFromSlug } from '../../../config/serverConfig';
import { HealthStatus, ReportFeedbackConfig, SpaceConfig } from '../../../config/spaces/spaceConfig';
import HealthStatusIndicator from './HealthStatusIndicator';
import { hasItems } from '../../../utils/collections';
import { FeedbackTag } from '../../../PublicFeedback';
import { Permission, UserRole } from '../../../config/permissions';
import { hasPermission } from '../../../config/accessPolicies';
import Draft, { clearDraft } from '../../shared/Draft';
import Secured from '../../shared/Secured';
import OutdatedBuildBlockDialog from '../../shared/OutdatedBuildDialog';
import { TelemetryResult } from '../../../telemetry/TelemetryHelper';
import { winFileTimeToJSDate, winFileTimeToJSTimeMsec } from '../../../utils/tools';
import { beforeInstallTemplate } from '../../../constants/templates';
import { PostType } from '../../../RootPost';
import { getSpaceIdFromSlug } from '../../../config/serverConfig';
import { Breadcrumb, IBreadcrumbItem } from '@fluentui/react/lib/Breadcrumb';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { v4 as uuidv4 } from 'uuid';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Dropdown, IButtonProps } from '@fluentui/react';
import SearchResultsAI from './SearchResultsAI';
import DevBoxInternalNote from '../../Notes/DevBoxInternalNote';
import AzureAppServiceNote from '../../Notes/AzureAppServiceNote';
import VsEngNote from '../../Notes/VsEngNote';
import DevBoxNote from '../../Notes/DevBoxNote';
import OfficeeEngineeringBanner from '../../Notes/OfficeEngineeringBanner';

const postTypesList = postTypes as unknown as string[];
const createTicketDraftKeyName = "ticket";
const createTicketDraftLabel = "reporting a new feedback item";
const createTicketDraftTitleField = "title";
const createTicketDraftDescriptionField = "description";

/** Current UI step for AI search */
enum AIStep {
    /** AI search is not enabled */
    notApplicable = "notApplicable",

    /** User is in the first page to enter problem */
    enter = "enter",

    /** User is in the second page to find solution */
    find = "find",

    /** User is in the third page to submit a ticket */
    submit = "submit",
}

/** Category options for feedback posts */
const categories: { key: string; text: string }[] = [
    { key: "copilot", text: "Copilot" },
    { key: "cpp", text: "C++" },
    { key: "dotnet", text: ".NET" },
    { key: "performance", text: "Performance" },
    { key: "editor", text: "Editor" },
    { key: "debugger", text: "Debugging" },
    { key: "installer", text: "Installer" },
    { key: "git", text: "Git Version Control" },
    { key: "signin", text: "Sign In/Authentication" },
    { key: "subscriptions", text: "Purchase/Subscriptions" },
    { key: "uwp", text: "UWP" },
    { key: "other", text: "Other" },
    { key: "unknown", text: "Not sure/Unknown" },
];

/** Information for a session tracking deflection rate
 * Used to assign a session ID and context info when opening report problem/suggest feature UI
 * to make it easier to track whether the user submitted a post, voted/commented/read a suggestion, 
 * left (closed the window, moved to anotehr place), or no-data
 */
export interface DeflectInfo {
    newPostType: PostType;
    deflectSessionId: string;
    experiments: string;
}

interface SubmitPostViewRouterParams {
    spaceSlug: string;
}
export interface SubmitPostViewProps {
}

enum Experiments {
    /** 2025-12-01: No experiments */
}

export default function SubmitPostView(props: SubmitPostViewProps): JSX.Element {
    const state = useSelector((state: RootState) => state.appState);
    const searchText = useSelector((state: RootState) => state.appState.problems.searchtext);
    const userId = useSelector((state: RootState) => state.appState.userVsid);
    const refSelectedPost = useRef(null);
    const hasBeforeInstallParam: boolean = useQueryParam(UrlParam.BeforeInstall) === "true";
    const { spaceSlug: spaceSlugParam } = useParams<SubmitPostViewRouterParams>();
    const productInfoException: string | null = useQueryParam(UrlParam.ProductInfoException);
    const history = useHistory();
    const [showSubmittingDialog, setShowSubmittingDialog] = useState(true);
    const [showBlockingDialog, setShowBlockingDialog] = useState(true);
    const [deflectInfo, setDeflectInfo] = useState({} as DeflectInfo);
    const [aiStep, setAiStep] = useState(AIStep.enter);
    const [draftShown, setDraftShown] = useState(false);
    const showCategories: boolean = useSelector((state: RootState) => state.appState.useCategories);

    enum breadcrumbNav {
        Explore,
        Submit,
    }
    const userRoles: UserRole[] = useSelector((state: RootState) => state.appState.userRoles);
    const isInPreview2: boolean = App.previewFeaturesEnabled || hasPermission(Permission.Preview2, userRoles);

    // determine new post type from URL
    const location = useLocation();
    const newPostType: PostType = (location.pathname.indexOf(AppPaths.report) >= 0) ? PostType.problem : PostType.suggestion;

    // Prevent from closing, reloading or navigating to different site if user has unsubmitted changes
    usePreventPageClose(state.rapReportWhatsDirty && state.rapReportWhatsDirty.length !== 0);

    const spaceIdFromRouter: SpaceId | null = getSpaceIdFromSlug(spaceSlugParam);

    useEffect(() => {
        Actions.setRapPostType(newPostType === PostType.problem ? EntryValue.problem : EntryValue.suggestion);
    }, [newPostType]);

    const joinOngoingDiscussion: boolean = state.dialog === Dialog.comment;
    useEffect(() => {
        if (newPostType === PostType.problem && !joinOngoingDiscussion) {
            setAiStep(AIStep.enter);
        } else {
            setAiStep(AIStep.notApplicable);
        }
    }, [newPostType, joinOngoingDiscussion]);

    const aiSearchEnabled: boolean = useMemo(
        () => aiStep !== AIStep.notApplicable,
        [aiStep]);

    useEffect(() => {
        Actions.setRapSpaceId(spaceIdFromRouter ?? SpaceId.VS);   // default to VS if spaceId query param is not specified or invalid
    }, [spaceIdFromRouter]);

    const spaceId = useSelector((state: RootState) => state.appState.reportSpaceId);
    const spaceConfig = useMemo(() => getSpaceConfig(spaceId), [spaceId]);
    const isBeforeInstall = hasBeforeInstallParam && spaceId === SpaceId.VS;

    useEffect(() => {
        // Clear form on load
        if (isBeforeInstall) {
            Actions.rapSetDetail(beforeInstallTemplate);
        }
        else {
            Actions.clearReportProblem();
        }

        // Reserve a new tracking ID in settings.template so that all attachments from RAP are associated with it.
        // Should be reset with new ID when report is submitted
        Actions.tryReserveNewTrackingId();
    }, [isBeforeInstall]);

    const onSearchRequested = useCallback(
        (searchFor: string, updateCountsByType: boolean, resultsToSkip: number): void => {
            // if AI search, don't do any non-AI suggestions search
            if (aiStep !== AIStep.notApplicable) {
                return;
            }

            const options: Actions.SearchOptions = new Actions.SearchOptions();
            options.spaceIds = spaceId ? [spaceId.toString()] : null;
            options.updateCountsByType = updateCountsByType;
            options.skip = resultsToSkip;
            options.take = 5;
            options.sort = Sort.relevance;
            Actions.searchFor(searchFor, SearchInfoTarget.all, options, "RAPSuggestions");
        }
        , [spaceId, aiStep]
    );

    const reportState = useSelector((state: RootState) => state.appState.reportState);

    useEffect(() => {
        if (reportState === OperationState.Success) {
            clearDraft(createTicketDraftKeyName);
        }
    }, [reportState]);

    const onSubmit = (state: AppState, reportType: ReportType, spaceConfig: SpaceConfig, reportConfig: ReportFeedbackConfig, isBeforeInstall: boolean): void => {
        let tags: FeedbackTag[] = spaceConfig?.spaceTag ? [spaceConfig.spaceTag] : [];

        const urlParams = new URLSearchParams(window.location.search);
        tags = [
            ...tags,
            ...tagParams
                .map(x => urlParams.has(x) ? ({ type: x, value: urlParams.get(x) } as FeedbackTag) : null)
                .filter(x => x)
        ];

        // if VS space and before install URL param set
        if (isBeforeInstall) {
            // send tags for before-install
            tags.push({ type: 'before-install', value: 'true' });
            tags.push({ type: 'product', value: 'setup.web' });
        }

        if (productInfoException === ProductInfoExceptionValues.oldDotNetLogs) {
            tags.push({ type: "product-info-exception", value: 'old-dot-net' });
        }

        // if not connected to VS or SSMS
        const isConnectedToVs: boolean = platformActions.getNativeActions() != null;
        if (!isConnectedToVs) {
            tags.push({ type: 'product', value: 'web' });
            tags.push({ type: 'product-build', value: 'web' });
            tags.push({ type: 'product-build-branch', value: 'web' });
        }

        if (showCategories) {
            tags.push({ type: 'category', value: state.reportCategory });
        }

        setShowSubmittingDialog(true);
        if (reportType === ReportType.newProblem) {
            App.telemetry.deflect(deflectInfo, "submit_feedback");
        }
        App.submitClick(state, reportType, tags, reportConfig.allowedTemplateTagTypes);
    };

    const onPostSubmissionDialogClose = (): void => {
        Actions.clearReportProblem();
        Actions.setDialog(Dialog.none);
        clearDraft(createTicketDraftKeyName);

        const params = new URLSearchParams(history.location.search);
        setTimeout(() => {
            history.push(`${AppPaths.myfeedback}?${params}`);
        }, 100);
    }

    const shouldShowPublicMessage = state.routingMessageShouldShow;


    const reportType: ReportType = joinOngoingDiscussion ? ReportType.vote : ReportType.newProblem;

    const signinState = useMemo(() => {
        if (state.signInState !== SignInState.Success) {
            return state.signInState;
        }

        // if sign in is done, check for user metadata loaded
        switch (state.userMetadataLoadState) {
            case OperationState.NotStarted:
            case OperationState.Executing:
                return SignInState.Executing;
            case OperationState.Failed:
                return SignInState.Failed;
            case OperationState.Success:
                // If user is signed in and metadata is loaded, update deflection state and UI state
                {
                    const newDeflectInfo: DeflectInfo = {
                        newPostType,
                        deflectSessionId: uuidv4(),
                        experiments: "",
                    };
                    setDeflectInfo(newDeflectInfo);
                    App.telemetry.deflect(newDeflectInfo, "show_new_feedback");
                }
                return SignInState.Success;
        }
    }, [state.userMetadataLoadState, state.signInState]);

    const nativeProcessAvailable = !!platformActions.getNativeActions();

    // If recording was started then we can safely submit only when
    // either the recording has been stopped and processed
    // or the recording was stopped and we are still processing it but it's safe to close VS
    const submissionBlockedByInProgressRecording: boolean =
        state.actionRecorderState === ActionRecorderState.started
        || (state.actionRecorderState === ActionRecorderState.processing && !state.actionRecorderSafeToCloseVS);

    // const isRecording: boolean = state.actionRecorderState === ActionRecorderState.started || state.actionRecorderState === ActionRecorderState.processing;
    const isCropping: boolean = state.reportIncludeScreenshot && state.reportIsCropping;

    const ignoreValidations = state.reportTitle.startsWith(testFeedbackTitlePrefix);
    const isCategoryFilled: boolean = state.reportCategory !== "";
    const isTitleLengthInvalid: boolean =
        !ignoreValidations
        && (
            !state.reportTitle
            || state.reportTitle.trim().length < minimumPostTitleLength
            || state.reportTitle.length > maximumPostTitleLength
        );
    const isTitleInvalid: boolean = state.dialog !== Dialog.comment && isTitleLengthInvalid;

    const isDescriptionLengthInvalid: boolean =
        !ignoreValidations
        && (
            !state.reportDetail
            || state.reportDetail.trim().length < minimumPostDetailsLength
        );
    const isDescriptionSameAsTitle: boolean = !ignoreValidations && state.reportTitle === state.reportDetail;
    const isDescriptionInvalid: boolean = isDescriptionLengthInvalid || isDescriptionSameAsTitle;

    const isPostTypeInvalid: boolean = !state.rapPostType || postTypesList.indexOf(state.rapPostType) === -1;
    const isSelectedItemInvalid: boolean = state.dialog === Dialog.comment && !state.selectedItemId;

    // nikolami: 2024-03-28: the function call is needed to workaround eslint bug that shows error 'react-hooks/rules-of-hooks' incorrectly if we have more than 5 condition on the same line
    const cantSubmit: boolean = (() => (submissionBlockedByInProgressRecording || isCropping || isTitleInvalid || isDescriptionInvalid || isPostTypeInvalid || isSelectedItemInvalid) || (showCategories && !isCategoryFilled))();
    const isRecordingEnabled: boolean = !App.settings?.isRecordingDisabled && state.rapPostType !== EntryValue.suggestion;

    const reportConfig = useMemo(
        () => state.rapPostType === EntryValue.suggestion ? spaceConfig.reportSuggestionConfig : spaceConfig.reportProblemConfig
        , [state.rapPostType, spaceConfig]
    );
    const nativeProcessIsRequired = (!isInPreview2 || reportConfig.isNativeProcessRequired === true) && !isBeforeInstall;
    const supportsNativeAttachments = (reportConfig.isNativeProcessRequired === true) // || nativeProcessAvailable; Nikola: 2025-01-28: uncomment to enable native attachments for SSMS

    // Read status messages
    useEffect(() => {
        // Read VS is required message when reporting VS problems
        App.read(nativeProcessIsRequired && !nativeProcessAvailable && ResourceStrings.noVSInstance());

        if (!nativeProcessIsRequired || nativeProcessAvailable) {
            // Read signin errors if any
            App.read(state.signInErrorMessage);
        }
    }, [nativeProcessAvailable, nativeProcessIsRequired, state.signInErrorMessage]);

    useEffect(
        () => {
            return () => { Actions.setDialog(Dialog.none); }
        }
        , []
    );

    useEffect(
        () => {
            Actions.setDialog(Dialog.none);
        }
        , [spaceId]
    );

    const [healthStatus, setHealthStatus] = useState<HealthStatus>();
    useEffect(() => {
        if (reportConfig?.healthStatus) {
            reportConfig.healthStatus()
                .then(x => { setHealthStatus(x); })
        } else if (healthStatus) {
            setHealthStatus(null);
        }
    }, [reportConfig]);

    const draftValue = useMemo(() => {
        if (!state.reportTitle && !state.reportDetail) {
            return null;
        }

        const data = new Map<string, string>();
        data.set(createTicketDraftTitleField, state.reportTitle);
        data.set(createTicketDraftDescriptionField, state.reportDetail);
        return data;
    }, [state.reportTitle, state.reportDetail]);

    const selectedItemsSpaceConfig: SpaceConfig = getSpaceConfig(state.selectedItem?.spaceId);

    const onOutdatedBuildDialogDismiss = (): void => {
        App.telemetry.postUserTask("OutdatedBuildDialogDismissed", TelemetryResult.Success, "", "", {
            userId: state.userVsid,
            action: ReleaseAction[state.releaseActionInfo.action],
            version: state.releaseActionInfo.version,
        });
        setShowBlockingDialog(false);
    };

    const onOutdatedBuildUpdateVisualStudio = (): void => {
        App.telemetry.postUserTask("OutdatedBuildVSUpdateLinkFollowed", TelemetryResult.Success, "", "", {
            userId: state.userVsid,
            action: ReleaseAction[state.releaseActionInfo.action],
            version: state.releaseActionInfo.version,
        });
    };

    const beforeInstallFooterTemplate: string = "Your user name, title, and description including the files and images which are embedded in the description of the initial report will be public on __DEVCOM__. Subsequent followups to the conversation can be made private if necessary. If you need completely private support, please contact your Customer Success Account Manager (CSAM).";

    // Office Offboarding: Redirect to a different page if the space is Office; should remove once offboarding process is finished
    if (spaceSlugParam === "office") {
        reportConfig.reportingNotAllowed = true;
        reportConfig.feedbackNotAllowedHtml = "Please note: ES365 Support has moved to <a href='https://aka.ms/OMRHelp'>https://aka.ms/OMRHelp</a>. We are no longer accepting Support tickets or Feature requests via EngCom."
    }
    // Office Offboarding

    if (reportConfig.reportingNotAllowed) {
        return <Wrapper data-cy="rap-submit-post-view">
            <PageContent>
                {/* We should never reach here normally. But keep it just in case user messes with the url */}
                <Notification>
                    {reportConfig.feedbackNotAllowedHtml && <div className="message"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: reportConfig.feedbackNotAllowedHtml }} />
                    }
                    {!reportConfig.feedbackNotAllowedHtml && <div className="message">{(state.rapPostType === EntryValue.suggestion) ? "Suggesting a feature" : "Reporting feedback"} is not allowed for &apos;{spaceConfig.displayName}&apos; space.</div>}
                </Notification>
            </PageContent>
        </Wrapper>
    }

    if (state.isUserBanned) {
        return <Wrapper data-cy="rap-submit-post-view">
            <PageContent>
                <Notification>
                    <div className="message">
                        You are banned from adding posts to Visual Studio Developer Community due to violations of our <a href="https://answers.microsoft.com/en-us/page/codeofconduct" target="_blank" rel="noopener noreferrer">Community Code of Conduct</a>.
                    </div>
                </Notification>
            </PageContent>
        </Wrapper>
    }
    function retainPathItems(numItems: number): string {
        if (numItems === 0) {
            return "";
        }
        const link = location.pathname;
        const splitLink = link.split('/');
        const retainedLink = splitLink.slice(0, numItems + 1).join('/');
        return retainedLink;
    }

    const homeText: string = "Home";
    const reportText: string = "Report a problem";
    const suggestText: string = "Suggest a feature";
    // If explore, get the space that was being explored, if submit get report or suggest
    function getPreviousPathItem(breadCrumbStage: breadcrumbNav): string {
        const defaultExploreText = "Explore";
        const splitLink = location.pathname.split('/');
        if (splitLink.length < 3) {
            return defaultExploreText;
        }
        else if (breadCrumbStage === breadcrumbNav.Submit) {
            if (splitLink[2] === 'report') {
                return reportText;
            }
            return suggestText;
        }
        const linkItem: string = getSpaceNameFromSlug(splitLink[1]) ?? "";
        return linkItem; // this logic will be replaced by react router in future tasks
    }
    const homePath: string = "/home"; // change if home link ever changes
    const breadcrumbItemStyle: CSSProperties = { color: 'var(--color-on-surface-link)', cursor: 'pointer', fontSize: '1.4rem' };
    const breadcrumbItems: IBreadcrumbItem[] = [
        {
            text: homeText, key: 'home', onClick: () => history.push(homePath), tabIndex: 0,
            style: breadcrumbItemStyle,
        },
        {
            text: getPreviousPathItem(breadcrumbNav.Explore), key: 'explore', onClick: () => history.push(retainPathItems(1)),
            style: breadcrumbItemStyle,
        },
        {
            text: getPreviousPathItem(breadcrumbNav.Submit), key: 'currentPage', isCurrentItem: true,
            style: { fontSize: '1.4rem' },
        },
    ];
    const headerText: string = (aiStep !== AIStep.notApplicable) ?
        "Describe your problem, search to see if it is known or we have a solution" :
        `${(state.rapPostType === EntryValue.suggestion ? "You are requesting a feature" : "You are reporting a problem")} for ${spaceConfig.displayName}`;

    const disabledPivotItemProps: IButtonProps = {
        disabled: true,
        style: { color: 'var(--color-on-surface-light)' }
    };

    const categoryChanged = (key: string): void => {
        Actions.rapSetCategory(key);
    }

    return <Wrapper data-cy="rap-submit-post-view">
        <div>
            <Breadcrumb
                style={{ padding: '0 0 0 calc(var(--page-margin-horizontal) - 0.8rem)' }}
                items={breadcrumbItems}
                ariaLabel="Breadcrumb with items"
            />
        </div>
        {/* Prevent navigation to non-RAP routes within app if user has unsubmitted changes */}
        <Prompt
            when={hasItems(state.rapReportWhatsDirty)}
            message={loc => loc.pathname === AppPaths.report ? true : 'You have some unsubmitted changes. Are you sure you want to leave this page?'}
        />


        {/* Report a problem page won't be possible if native isn't available */}
        {nativeProcessIsRequired && !nativeProcessAvailable && <PageContent>
            <div css="padding: 0 1rem">
                <h1 css="font-size: 2.4rem">How to report a problem for Visual Studio and Visual Studio Installer</h1>

                <div css="margin-top: 2rem">
                    If you are using Visual Studio on Windows, submit your issue from within Visual Studio or Visual Studio Installer.
                    It creates a diagnostically rich and highly actionable report that increases the chances of investigation and resolution.
                </div>

                <div css="margin-top: 2rem">
                    <ol>
                        <li css="display: list-item; max-width: 100%">
                            <div>From the main menu in Visual Studio, select <strong>Help &gt; Send Feedback &gt; Report a Problem</strong>.</div>
                            <div css="padding: 1rem 2rem 3rem 4rem"><img src="/images/ReportProblemVS.png" alt="Reporting problem from Visual Studio" /></div>
                            <div>Or to submit a problem from Visual Studio Installer, select <strong>Provide Feedback icon &gt; Report a problem</strong>.</div>
                            <div css="padding: 1rem 2rem 3rem 4rem"><img src="/images/ReportProblemVSInstaller.png" alt="Reporting problem from Visual Studio Installer" /></div>
                        </li>

                        <li css="display: list-item">Follow the onscreen instructions and submit your problem. You&apos;ll receive a confirmation email with a link for the  submitted problem.</li>
                    </ol>
                </div>
            </div>
        </PageContent>}

        {(!nativeProcessIsRequired || nativeProcessAvailable) && <>
            {<AuthenticationMessages
                signinState={signinState}
                signInErrorMessage={state.signInErrorMessage}
                render={(props: ChildrenProps): JSX.Element => <PageContent>{props.children}</PageContent>} />
            }

            {/* TODO: Clean up warn/block logic and strings once we've decided on warn functionality */}
            {signinState === SignInState.Success && <>
                {state.releaseActionInfo.action === ReleaseAction.Block && nativeProcessIsRequired && <OutdatedBuildBlockDialog
                    releaseActionInfo={state.releaseActionInfo}
                    isOpen={showBlockingDialog}
                    showContinueButton={true}
                    onClose={onOutdatedBuildDialogDismiss}
                    onUpdate={onOutdatedBuildUpdateVisualStudio}
                />}
                <Content>
                    <Title>
                        {!joinOngoingDiscussion && !isBeforeInstall && <h2 className="semibold top-level-text">{headerText}</h2>}
                        {!joinOngoingDiscussion && isBeforeInstall && <h2 className="semibold top-level-text before-install">{state.rapPostType === EntryValue.suggestion ? "You are requesting a feature" : " You are reporting a problem"} for a {spaceConfig.displayName} administrator or deployment scenario</h2>}
                        {joinOngoingDiscussion && <h2 className="semibold top-level-text">You are voting and providing more info for a &apos;{selectedItemsSpaceConfig?.displayName}&apos; {state.rapPostType === EntryValue.suggestion ? "feature request" : "problem"}</h2>}

                        <span className="secured-message top-level-text"><Secured /></span>
                    </Title>

                    <OfficeeEngineeringBanner slug={spaceConfig.slug} />

                    <DevBoxNote slug={spaceConfig.slug} />

                    <AzureAppServiceNote slug={spaceConfig.slug} />

                    <VsEngNote slug={spaceConfig.slug} />

                    <DevBoxInternalNote slug={spaceConfig.slug} />

                    {aiSearchEnabled && <>
                        <Pivot
                            selectedKey={aiStep}
                            onLinkClick={(item, ev): void => {
                                if (item.props.itemKey === AIStep.enter) {
                                    setAiStep(AIStep.enter);
                                }
                                else if (item.props.itemKey === AIStep.find && aiStep === AIStep.submit) {
                                    setAiStep(AIStep.find);
                                }
                            }}
                        >
                            <PivotItem headerText="1. Enter your problem" itemKey={AIStep.enter} />
                            <PivotItem headerText="2. Recommended solution" itemKey={AIStep.find} headerButtonProps={aiStep === AIStep.enter ? disabledPivotItemProps : {}} />
                            <PivotItem headerText="3. Submit a ticket" itemKey={AIStep.submit} headerButtonProps={aiStep !== AIStep.submit ? disabledPivotItemProps : {}} />
                        </Pivot>
                    </>}

                    {(!aiSearchEnabled || aiStep !== AIStep.find) && <div className="form-wrapper">
                        {shouldShowPublicMessage && <MessageBar messageBarType={MessageBarType.warning} tabIndex={0}>
                            <span>{ResourceStrings.publicProblem()}</span>
                        </MessageBar>}

                        {healthStatus && <HealthStatusIndicator status={healthStatus} />}

                        {!joinOngoingDiscussion && !aiSearchEnabled && <GoodReportInstructions forSuggestion={state.rapPostType === EntryValue.suggestion} additionalGuidance={reportConfig.additionalGuidance} />}

                        {!joinOngoingDiscussion && <Draft
                            canShow={!draftShown}
                            name={createTicketDraftKeyName}
                            label={createTicketDraftLabel}
                            fieldNames={[createTicketDraftTitleField, createTicketDraftDescriptionField]}
                            data={draftValue}
                            onDraftLoaded={(data: Map<string, string>) => {
                                const title = data.get(createTicketDraftTitleField) ?? "";
                                const description = data.get(createTicketDraftDescriptionField) ?? "";

                                Actions.rapSetTitle(title);
                                Actions.searchUpdate(title);

                                Actions.evUpdateCommentText(description);
                                Actions.rapSetDetail(description);
                                setDraftShown(true);
                            }}
                        />}

                        <LogRapPageLoadTelemetry spaceConfig={spaceConfig} reportType={reportType} nativeProcessIsRequired={nativeProcessIsRequired} nativeProcessAvailable={nativeProcessAvailable} />

                        {isBeforeInstall && <GuideInfo>
                            To help us investigate this issue, please run <a href="https://aka.ms/vscollect" target="_blank">https://aka.ms/vscollect</a> tool on the affected machine that has Visual Studio on it. Attach the resultant file to this report.
                        </GuideInfo>}

                        {joinOngoingDiscussion && <Panel ref={refSelectedPost}>
                            <Accordion
                                highlightWhenCollapsed
                                headerClassName="selected-post-header"
                                data-cy="rap-selected-post"
                                titleIcon="SingleColumnEdit"
                                // eslint-disable-next-line react/no-danger
                                title={<span dangerouslySetInnerHTML={{ __html: App.EscapeHtmlRawDisplay(state.selectedItem.title) }} />}
                            >
                                <div css="padding: 1.4rem 4.8rem 2.8rem 4.8rem">
                                    <PostDetail
                                        deflectInfo={deflectInfo}
                                        post={state.selectedItem}
                                        postV3={state.ticketsById[state.selectedItem?.answerHubId]}
                                        userId={userId}
                                    />
                                </div>
                            </Accordion>
                        </Panel>}

                        {!joinOngoingDiscussion && <div>
                            <label className="title-instruction semibold required-field top-level-text block-label" htmlFor="title-editor">
                                {ResourceStrings.providePostDetailsText()}
                            </label>

                            <TitleEditor id="title-editor" onSearch={onSearchRequested} placeholderText={reportConfig?.titlePlaceholder} ariaLabelText={ResourceStrings.providePostDetailsText()} />
                            {!aiSearchEnabled && <Panel key={`search-result-${searchText}`} css="margin-top: 2rem"><SearchResults deflectInfo={deflectInfo} onSearch={onSearchRequested} /></Panel>}
                        </div>}

                        {showCategories && <div>
                            <div className="title-instruction top-level-text block-label">
                                <span className="semibold required-field">Select a category</span>
                            </div>
                            <Dropdown
                                placeholder={"Select a category (required)"}
                                ariaLabel={"Select a category (required)"}
                                options={categories}
                                selectedKey={state.reportCategory}
                                onChange={(evt, item) => categoryChanged(item.key as string)}
                            />
                        </div>}

                        <div>
                            <div className="title-instruction top-level-text block-label">
                                <span className="semibold required-field">{ResourceStrings.description()}</span>

                                {/* Not applicable for internal instance */}
                                {!instance.isInternal && <a href={codeOfConduct} className="code-of-conduct" target="_blank" rel="noopener noreferrer">Community Code of Conduct</a>}
                            </div>
                            <DescriptionEditor forNeedMoreInfo={false} reportType={reportType} reportConfig={reportConfig} spaceConfig={joinOngoingDiscussion ? selectedItemsSpaceConfig : spaceConfig} />
                        </div>

                        {/* Check whether to show submit UI: shown either if original (non-AI search) or if on the submit page of AI search */}
                        {(!aiSearchEnabled || aiStep === AIStep.submit) && <>
                            {supportsNativeAttachments && nativeProcessAvailable && <Expandable><ScreenshotEditor reportType={reportType} /></Expandable>}

                            {supportsNativeAttachments && nativeProcessAvailable && isRecordingEnabled && state.showRecordTip && <RecordingTip>
                                <Icon iconName="Warning" />
                                {ResourceStrings.recordTip()}
                            </RecordingTip>}

                            {supportsNativeAttachments && nativeProcessAvailable && isRecordingEnabled && <Expandable><ActionRecorder reportType={reportType} /></Expandable>}

                            {supportsNativeAttachments && nativeProcessAvailable && <Expandable><AttachmentList reportType={reportType} /></Expandable>}

                            <Footer className="top-level-text">
                                {isBeforeInstall && <PrivacyDisclosure
                                    includesNativeAttachments={nativeProcessIsRequired}
                                    disclosureLink={beforeInstallFooterTemplate.replace("__DEVCOM__", App.GetPublicFeedbackSiteLink())}
                                    disclosure={beforeInstallFooterTemplate.replace("__DEVCOM__", ResourceStrings.publicFeedbackSite())}
                                />}
                                {!isBeforeInstall && <PrivacyDisclosure
                                    includesNativeAttachments={nativeProcessIsRequired}
                                    disclosureLink={ResourceStrings.publicDisclosureProblem(App.GetPublicFeedbackSiteLink())}
                                    disclosure={ResourceStrings.publicDisclosureProblem(ResourceStrings.publicFeedbackSite())}
                                />}
                            </Footer>
                            {/* @TODO: Show tooltip when disabled to state the reason */}
                            {(!aiSearchEnabled || aiStep === AIStep.submit) && <Button disabled={cantSubmit} onClick={(): void => onSubmit(state, reportType, spaceConfig, reportConfig, isBeforeInstall)}>{ResourceStrings.submit()}</Button>}
                        </>}

                        {aiStep === AIStep.enter && <Button disabled={cantSubmit} onClick={(): void => {
                            App.telemetry.deflect(deflectInfo, "search_feedback");
                            setDraftShown(true);
                            Actions.vectorSearch(state.reportTitle, state.reportDetail, spaceId, "RapPage");
                            setAiStep(AIStep.find);
                        }}>Search</Button>}
                    </div>}

                    {aiStep === AIStep.find && <div className="form-wrapper">
                        <h2>Solutions and similar tickets</h2>
                        <p>If your issue is already listed below, do not create a new one. Instead please upvote the existing one. This will help us prioritize it and notify you when it is solved. You can also comment on it to share more details or workarounds.</p>
                        {state.vectorSuggestions.state === OperationState.Executing && <>
                            <Spinner size={SpinnerSize.large} />
                        </>}

                        {state.vectorSuggestions.state === OperationState.Success && <>
                            <p>✅Found {state.vectorSuggestions.results.results.length} similar tickets and solutions</p>
                            <SearchResultsAI deflectInfo={deflectInfo} results={state.vectorSuggestions.results.results} />
                        </>}

                        {state.vectorSuggestions.state === OperationState.Failed && <>
                            <Error>Error finding similar tickets and solutions: {state.vectorSuggestions.errorMessage}</Error>
                        </>}

                        {(state.vectorSuggestions.state === OperationState.Success || state.vectorSuggestions.state === OperationState.Failed) && <>
                            <Button onClick={(): void => {
                                App.telemetry.deflect(deflectInfo, "continue_no_solution");
                                setAiStep(AIStep.submit)
                            }}>{"Can't find the solution"}</Button>
                        </>}

                    </div>}
                </Content>

                <NotificationDialog
                    notificationType="information"
                    isOpen={(state.reportState === OperationState.Executing
                        || state.detailAddCommentState === OperationState.Executing
                        || state.screenRecordingUploadState === OperationState.Executing
                    ) && showSubmittingDialog}
                    message={state.reportState === OperationState.Executing ?
                        (
                            state.screenRecordingUploadState === OperationState.Executing ?
                                `Uploading screen recording, please wait... (${state.screenRecordingPercentDone}%)`
                                : ResourceStrings.sendingReportPleaseWait()
                        )
                        : ResourceStrings.votePleaseWait()}
                    canDismiss
                    isBlocking
                    showSpinner
                    onDismiss={(): void => setShowSubmittingDialog(false)}
                />

                <CommentSubmittedDialog
                    answerHubId={state.selectedItemId}
                    communityUrl={App.getTicketPath(state.selectedItemId, state.selectedItem?.title ?? "")}
                    isOpen={state.detailAddCommentState === OperationState.Success && joinOngoingDiscussion}
                    onClose={onPostSubmissionDialogClose}
                />
                <PostSubmittedDialog
                    isOpen={(state.reportState === OperationState.Success) && !joinOngoingDiscussion}
                    onClose={onPostSubmissionDialogClose}
                />
            </>}
        </>}
    </Wrapper >
}

// Global var to ensure initial page load telemetry is reported only once
let isRapPageLoadTelemetryReported: boolean = false;

interface LogRapPageLoadTelemetryProps {
    spaceConfig: SpaceConfig;
    reportType: ReportType;
    nativeProcessIsRequired: boolean;
    nativeProcessAvailable: boolean;
}

// Component to log perf telemetry for RAP page being ready
const LogRapPageLoadTelemetry = (props: LogRapPageLoadTelemetryProps): JSX.Element => {
    const loadStartMsec = useSelector((state: RootState) => state.appState.settings.loadStartMsec);

    useEffect(() => {
        if (loadStartMsec && !isRapPageLoadTelemetryReported) {
            App.telemetry.postUserTask("RapPageLoadPerf", TelemetryResult.Success, "", "", {
                loadStartMsec: loadStartMsec.toString(),
                loadStartDate: winFileTimeToJSDate(loadStartMsec).toISOString(),
                pageReadyDurationMsec: (Date.now() - winFileTimeToJSTimeMsec(loadStartMsec)).toString(),
                spaceId: props.spaceConfig?.id?.toString(),
                reportType: props.reportType,
                nativeProcessIsRequired: props.nativeProcessIsRequired?.toString(),
                nativeProcessAvailable: props.nativeProcessAvailable?.toString(),
            });

            isRapPageLoadTelemetryReported = true;
        }
    }, [loadStartMsec]);

    return null;
}

const Wrapper = styled.div`
    background-color: var(--color-background);
    position: relative;
    h2 {
        font-size: 2.4rem;
    }
`;

const GuideInfo = styled.div`
    background: #f9e766;
    padding: 1rem;
    border-radius: 0.4rem;
    margin-bottom: 1rem;
`;

const RecordingTip = styled.div`
    display: flex;
    align-items: center;
    background-color: var(--color-surface-dark2);
    color: var(--color-surface);
    font-size: 1.8rem;
    line-height: 2.4rem;
    font-weight: 600;
    padding: 1.2rem 2rem;

    i {
        margin-right: 2rem;
        font-size: 2.5rem;
    }
`;

const Title = styled.div`
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    align-items: baseline;

    >* + * {
        margin-top: .6rem;
    }

    .before-install {
        font-size: 1.2em;
    }
    
    ${forMinWidth.sm`
        flex-direction: row;

        >* + * {
            margin-top: 0;
        }
    `}
`;

const Content = styled(PageContent)`
    .form-wrapper {
        background-color: var(--color-surface);
        padding: 3.5rem;
        box-shadow: 0px 0.3px 0.9px rgb(0 0 0 / 11%), 0px 1.6px 3.6px rgb(0 0 0 / 13%);

        >* + * {
            margin-top: 3rem;
        }
    }

    .title-instruction {
        font-size: 1.4rem;
        line-height: 2.4rem;

        .btn-link {
            padding: 0;
            height: auto;
        }
    }

    .selected-post-header {
        font-size: 2.4rem;
        padding-top: 1.2rem;
        padding-bottom: 1.2rem;
    }

    .code-of-conduct {
        margin-left: 1.4rem;
        font-size: 1.5rem;
    }

    .block-label {
        display: inline-block;
        margin-bottom: .3rem;
    }
`;

const Footer = styled.footer`
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    align-items: flex-end;
    margin-top: 4rem;

    button {
        margin-top: 2rem;
        width: fit-content;
    }

    ${forMinWidth.md`
        flex-direction: row;
        align-items: flex-start;

        button {
            margin-top: 0;
            margin-left: 3rem;
        }
    `}
`;

const Expandable = styled(Panel)`
    box-shadow: none;
    border: solid 1px var(--color-on-surface-light);

    .accordion-header {
        height: 2rem;

        span {
            font-size: 1.4rem;
        }
    }
`;

const Error = styled.div`
    color: var(--color-error);
`;